
import React, { useState } from 'react';
import { Mail, Phone, User, Briefcase, MessageSquare, CheckCircle, AlertCircle } from 'lucide-react';
import FormSubmissionHandler, { FormData } from './FormSubmissionHandler';
import { FormService } from '../services/formService';

interface SignupFormProps {
  className?: string;
  title?: string;
}

const SignupForm: React.FC<SignupFormProps> = ({ 
  className = '', 
  title = 'Stay Updated with Savings & Assistance' 
}) => {
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    profession: '',
    hearAbout: '',
    emailConsent: false,
    textConsent: false
  });

  const [validationErrors, setValidationErrors] = useState<Partial<FormData>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Clear validation error when user starts typing
    if (validationErrors[name as keyof FormData]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const validateForm = (data: FormData): Partial<FormData> => {
    const errors: Partial<FormData> = {};

    if (!data.firstName.trim()) {
      errors.firstName = 'First name is required' as any;
    }

    if (!data.lastName.trim()) {
      errors.lastName = 'Last name is required' as any;
    }

    if (!data.email.trim()) {
      errors.email = 'Email address is required' as any;
    } else if (!FormService.validateEmail(data.email)) {
      errors.email = 'Please enter a valid email address' as any;
    }

    if (data.phone && !FormService.validatePhone(data.phone)) {
      errors.phone = 'Please enter a valid phone number' as any;
    }

    return errors;
  };

  const handleSubmit = async (data: FormData) => {
    const errors = validateForm(data);
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      throw new Error('Please fix the validation errors');
    }

    setValidationErrors({});
    await FormService.submitSignup(data);
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      profession: '',
      hearAbout: '',
      emailConsent: false,
      textConsent: false
    });
    setValidationErrors({});
  };

  return (
    <FormSubmissionHandler onSubmit={handleSubmit}>
      {({ isSubmitting, isSubmitted, error, handleSubmit: submitForm, resetForm: resetHandler }) => {
        if (isSubmitted) {
          return (
            <div className={`bg-white rounded-lg shadow-lg p-6 ${className}`}>
              <div className="text-center">
                <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                <h3 className="text-2xl font-semibold text-gray-900 mb-2">Thank You!</h3>
                <p className="text-gray-600 mb-6">
                  You've successfully signed up for updates on assistance programs, discounts, and financial planning tips.
                  You'll receive your first email within 24 hours.
                </p>
                <button
                  onClick={() => {
                    resetHandler();
                    resetForm();
                  }}
                  className="btn-secondary"
                >
                  Sign Up Another Person
                </button>
              </div>
            </div>
          );
        }

        return (
          <div className={`bg-white rounded-lg shadow-lg p-6 ${className}`}>
            <h3 className="text-xl font-semibold text-green-700 mb-4">{title}</h3>
            <p className="text-gray-600 mb-6">
              Get the latest information on assistance programs, discounts, and financial planning tips delivered to your inbox.
            </p>
            
            <form 
              onSubmit={(e) => {
                e.preventDefault();
                submitForm(formData);
              }} 
              className="space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <User className="w-4 h-4 inline mr-1" />
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    required
                    value={formData.firstName}
                    onChange={handleChange}
                    className={`input-field ${validationErrors.firstName ? 'input-error' : ''}`}
                    disabled={isSubmitting}
                  />
                  {validationErrors.firstName && (
                    <p className="error-message">{validationErrors.firstName}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <User className="w-4 h-4 inline mr-1" />
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    required
                    value={formData.lastName}
                    onChange={handleChange}
                    className={`input-field ${validationErrors.lastName ? 'input-error' : ''}`}
                    disabled={isSubmitting}
                  />
                  {validationErrors.lastName && (
                    <p className="error-message">{validationErrors.lastName}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className={`input-field ${validationErrors.email ? 'input-error' : ''}`}
                  disabled={isSubmitting}
                />
                {validationErrors.email && (
                  <p className="error-message">{validationErrors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Phone className="w-4 h-4 inline mr-1" />
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`input-field ${validationErrors.phone ? 'input-error' : ''}`}
                  disabled={isSubmitting}
                  placeholder="(Optional)"
                />
                {validationErrors.phone && (
                  <p className="error-message">{validationErrors.phone}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Briefcase className="w-4 h-4 inline mr-1" />
                  Profession/Occupation
                </label>
                <input
                  type="text"
                  name="profession"
                  value={formData.profession}
                  onChange={handleChange}
                  className="input-field"
                  disabled={isSubmitting}
                  placeholder="(Optional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <MessageSquare className="w-4 h-4 inline mr-1" />
                  How did you hear about us?
                </label>
                <select
                  name="hearAbout"
                  value={formData.hearAbout}
                  onChange={handleChange}
                  className="input-field"
                  disabled={isSubmitting}
                >
                  <option value="">Select an option (Optional)</option>
                  <option value="search-engine">Search Engine (Google, Bing, etc.)</option>
                  <option value="social-media">Social Media (Facebook, Twitter, etc.)</option>
                  <option value="friend-family">Friend or Family Member</option>
                  <option value="government-agency">Government Agency</option>
                  <option value="nonprofit">Nonprofit Organization</option>
                  <option value="advertisement">Advertisement</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="border-t pt-4">
                <p className="text-sm font-medium text-gray-700 mb-3">Communication Preferences *</p>
                
                <div className="space-y-3">
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      name="emailConsent"
                      checked={formData.emailConsent}
                      onChange={handleChange}
                      className="mt-1 mr-3 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      disabled={isSubmitting}
                    />
                    <span className="text-sm text-gray-600">
                      <strong>Yes, I want to receive emails</strong> about assistance programs, discounts, and financial planning tips. 
                      I understand I can unsubscribe at any time by clicking the unsubscribe link in any email.
                    </span>
                  </label>
                  
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      name="textConsent"
                      checked={formData.textConsent}
                      onChange={handleChange}
                      className="mt-1 mr-3 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      disabled={isSubmitting}
                    />
                    <span className="text-sm text-gray-600">
                      <strong>Yes, I want to receive text messages</strong> about urgent assistance opportunities and time-sensitive discounts. 
                      Message and data rates may apply. Text STOP to opt out at any time.
                    </span>
                  </label>
                </div>

                {!formData.emailConsent && !formData.textConsent && (
                  <p className="text-sm text-amber-600 mt-2 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    Please select at least one communication method to continue.
                  </p>
                )}
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-red-600 text-sm flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {error}
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting || (!formData.emailConsent && !formData.textConsent)}
                className="w-full bg-green-600 text-white py-3 px-4 rounded-md hover:bg-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <div className="spinner mr-2"></div>
                    Signing Up...
                  </>
                ) : (
                  'Sign Up for Updates'
                )}
              </button>

              <p className="text-xs text-gray-500 mt-2">
                By signing up, you agree to our <a href="/privacy-policy" className="text-green-600 hover:underline">Privacy Policy</a> and 
                <a href="/terms-of-service" className="text-green-600 hover:underline ml-1">Terms of Service</a>. 
                We will never sell or share your personal information with third parties.
              </p>
            </form>
          </div>
        );
      }}
    </FormSubmissionHandler>
  );
};

export default SignupForm;
